import axios from "axios";
import { API_BASE_URL } from "./constants";
import logger from "./logger";
import errorHandler from "./errorHandler";

// Create axios instance with default timeout
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000, // Default 15 seconds
  withCredentials: true, // Send httpOnly JWT cookie with every request
});

let globalLoadingListener = null;
let activeGlobalLoadingRequests = 0;

const notifyGlobalLoading = () => {
  if (typeof globalLoadingListener === "function") {
    globalLoadingListener(activeGlobalLoadingRequests > 0);
  }
};

const incrementGlobalLoading = () => {
  activeGlobalLoadingRequests += 1;
  notifyGlobalLoading();
};

const decrementGlobalLoading = () => {
  activeGlobalLoadingRequests = Math.max(0, activeGlobalLoadingRequests - 1);
  notifyGlobalLoading();
};

const shouldTrackGlobalLoading = (config) => {
  if (config?.suppressGlobalLoading || config?.meta?.suppressGlobalLoading) {
    return false;
  }

  if (config?.showGlobalLoading === true) {
    return true;
  }

  // Skip global loading for AI assistant queries (has its own in-panel indicator)
  const url = config?.url || "";
  if (url.includes("/ai/")) return false;

  const method = (config?.method || "get").toLowerCase();
  const isMutationRequest = ["post", "put", "patch", "delete"].includes(method);
  const contentType =
    config?.headers?.["Content-Type"] ||
    config?.headers?.["content-type"] ||
    "";
  const isUploadRequest = contentType.includes("multipart/form-data");

  return isMutationRequest || isUploadRequest;
};

export const setGlobalLoadingListener = (listener) => {
  globalLoadingListener = listener;
};

// Extended timeout for slow operations (email sending, file processing)
const EXTENDED_TIMEOUT = 45000; // 45 seconds

// Endpoints that need extended timeout (email operations, uploads, etc.)
const EXTENDED_TIMEOUT_ENDPOINTS = [
  "/auth/forgot-password",
  "/auth/reset-password",
  "/uploads",
  "/api/uploads",
  "/send-email", // Email with PDF attachment endpoints
  "/quotations/.*?/send-email",
  "/billing/.*?/send-email",
];

// Check if endpoint needs extended timeout
function needsExtendedTimeout(url) {
  return EXTENDED_TIMEOUT_ENDPOINTS.some(
    (endpoint) => url && url.includes(endpoint),
  );
}

// Request interceptor - Add auth token to requests (via httpOnly cookie, withCredentials handles it)
api.interceptors.request.use(
  (config) => {
    if (shouldTrackGlobalLoading(config)) {
      config.__trackGlobalLoading = true;
      incrementGlobalLoading();
    }

    // Track current page for presence (low-latency user log) — sent on every request
    try {
      if (typeof window !== 'undefined' && window.location) {
        const curPage = window.location.pathname + (window.location.search || '');
        if (curPage) {
          config.headers = config.headers || {};
          config.headers['x-current-page'] = curPage.slice(0, 300);
        }
      }
    } catch {}

    // Apply extended timeout for specific endpoints
    if (needsExtendedTimeout(config.url)) {
      config.timeout = EXTENDED_TIMEOUT;
      logger.debug(
        `API Request: ${config.method?.toUpperCase()} ${config.url} [Extended timeout: ${EXTENDED_TIMEOUT}ms]`,
        null,
        "API",
      );
    } else {
      logger.debug(
        `API Request: ${config.method?.toUpperCase()} ${config.url}`,
        null,
        "API",
      );
    }

    return config;
  },
  (error) => {
    logger.error("API Request Error", error, "API");
    return Promise.reject(error);
  },
);

// --- 429 resilience: one GET retry honoring Retry-After + dedupe of identical in-flight GETs ---
const inflightGets = new Map();

const getRetryAfterMs = (error) => {
  const raw =
    error?.response?.headers?.["retry-after"] ??
    error?.response?.headers?.["Retry-After"];
  const secs = Number(raw);
  if (Number.isFinite(secs) && secs >= 0) {
    return Math.min(secs * 1000, 120000);
  }
  return 5000;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Response interceptor - Handle errors globally
api.interceptors.response.use(
  (response) => {
    if (response?.config?.__trackGlobalLoading) {
      decrementGlobalLoading();
    }

    logger.debug(
      `API Response: ${response.status} ${response.config.url}`,
      null,
      "API",
    );
    return response;
  },
  async (error) => {
    if (error?.config?.__trackGlobalLoading) {
      decrementGlobalLoading();
    }

    // Single retry on 429 for idempotent GETs, honoring the server's
    // Retry-After header (capped at 15s). Prevents every component retrying
    // at once and turning a brief throttle into a sustained storm.
    // Mutations (POST/PUT/PATCH/DELETE) are never auto-retried.
    const config = error?.config;
    const method = (config?.method || "get").toLowerCase();
    if (
      error?.response?.status === 429 &&
      config &&
      !config.__retry429 &&
      (method === "get" || method === "head" || method === "options")
    ) {
      config.__retry429 = true;
      const waitMs = Math.min(getRetryAfterMs(error), 15000);
      logger.warn(
        `Rate limited (429) on ${config.url}, retrying once after ${Math.ceil(waitMs / 1000)}s`,
        null,
        "API",
      );
      await sleep(waitMs);
      try {
        return await api.request(config);
      } catch (retryError) {
        return Promise.reject(retryError);
      }
    }

    // Handle timeout errors
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      logger.error(
        "Request timeout",
        {
          url: error.config?.url,
          timeout: error.config?.timeout,
        },
        "API",
      );
      // Don't redirect, just log and reject so UI can handle it
    }

    // Handle authentication failures
    const status = error.response?.status;
    const respMessage = (error.response?.data?.message || "")
      .toString()
      .toLowerCase();
    const respError = (error.response?.data?.error || "")
      .toString()
      .toLowerCase();

    // Treat 401 as auth failure. Also treat specific 403 responses that indicate
    // an invalid/expired token (e.g. 'Invalid or expired token' or 'invalid signature')
    // as auth failures so client clears session and redirects to login.
    // Exception: skip redirect for auth endpoints (login/register) — a 401 there
    // means wrong credentials, not an expired session.
    const requestUrl = error.config?.url || "";
    const isAuthEndpoint =
      requestUrl.includes("/auth/login") ||
      requestUrl.includes("/auth/register") ||
      requestUrl.includes("/auth/google") ||
      requestUrl.includes("/auth/me");
    if (
      !isAuthEndpoint &&
      (status === 401 ||
        (status === 403 &&
          (respMessage.includes("invalid or expired token") ||
            respError.includes("invalid signature"))))
    ) {
      // Clear user session data (the httpOnly cookie is cleared by backend on explicit logout,
      // but on 401 we clear the Redux state to redirect to login)
      try {
        // Dispatch logout action via store to clear Redux state
        const { store } = require("../redux/store");
        store.dispatch({ type: "auth/logout" });
      } catch (e) {
        // ignore
      }
      logger.info(
        "Session expired/invalid token, redirecting to login",
        null,
        "API",
      );
      window.location.href = "/login";
    } else if (status === 403) {
      logger.warn("Access forbidden", { url: error.config?.url }, "API");
    }

    // Handle 404 Not Found
    if (error.response?.status === 404) {
      logger.debug("Resource not found", { url: error.config?.url }, "API");
    }

    // Handle 500 Server Error
    if (error.response?.status >= 500) {
      logger.error(
        "Server error occurred",
        {
          status: error.response?.status,
          url: error.config?.url,
          message:
            error.response?.data?.message ||
            error.response?.data?.error ||
            error.message,
        },
        "API",
      );
    }

    return Promise.reject(error);
  },
);

// Short TTL cache for slow-changing reads (public settings, AI status, VAPID
// key). Dashboard mounts fire these on every visit + StrictMode remounts —
// serving a seconds-old copy avoids the network roundtrip entirely.
// Mutating callers must call apiHelper.invalidateGet(url) after a successful
// write if they need immediate freshness.
const ttlCache = new Map();
const TTL_BY_URL = {
  "/site-settings": 60 * 1000,
  "/plans/settings": 60 * 1000,
  "/push/vapid-public-key": 5 * 60 * 1000,
  "/ai/status": 30 * 1000,
};

const getTtl = (url) => {
  if (!url) return 0;
  for (const [prefix, ttl] of Object.entries(TTL_BY_URL)) {
    if (url === prefix || url.startsWith(prefix + "?")) return ttl;
  }
  return 0;
};

// API helper functions
export const apiHelper = {
  // Drop a cached GET so the next read hits the network (call after writes)
  invalidateGet: (url) => {
    if (!url) return;
    ttlCache.delete(`GET ${url}`);
  },

  // GET request — identical in-flight GETs (same url, no params) share one
  // network call so parallel mounts (e.g. two PlanCards, StrictMode) don't
  // double the request count.
  get: async (url, config = {}) => {
    const canDedupe = !config?.params && !config?.data;
    const key = canDedupe ? `GET ${url}` : null;
    if (key) {
      if (inflightGets.has(key)) {
        return inflightGets.get(key);
      }
      const ttl = getTtl(url);
      const hit = ttlCache.get(key);
      if (ttl > 0 && hit && Date.now() - hit.at < ttl) {
        return hit.data;
      }
    }
    const promise = (async () => {
      try {
        const response = await api.get(url, config);
        const data = response.data;
        if (key) {
          const ttl = getTtl(url);
          if (ttl > 0) {
            ttlCache.set(key, { at: Date.now(), data });
            if (ttlCache.size > 100) {
              const oldest = ttlCache.keys().next().value;
              ttlCache.delete(oldest);
            }
          }
        }
        return data;
      } catch (error) {
        throw error;
      } finally {
        if (key) inflightGets.delete(key);
      }
    })();
    if (key) inflightGets.set(key, promise);
    return promise;
  },

  // POST request
  post: async (url, data = {}, config = {}) => {
    try {
      const response = await api.post(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PUT request
  put: async (url, data = {}, config = {}) => {
    try {
      const response = await api.put(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // PATCH request
  patch: async (url, data = {}, config = {}) => {
    try {
      const response = await api.patch(url, data, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // DELETE request
  delete: async (url, config = {}) => {
    try {
      const response = await api.delete(url, config);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload file
  upload: async (url, formData, onUploadProgress) => {
    try {
      const response = await api.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress,
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
